import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle, ArrowRight, BadgeCheck, Check, Copy, IndianRupee, Inbox,
  Loader2, Pencil, Radar, Send, ShieldCheck, X,
} from "lucide-react";
import { api } from "@/lib/api";
import AgentAvatar from "@/components/AgentAvatar";

const fmtINR = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

const STATUS_META = {
  pending_review: { label: "Awaiting your approval", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  sent: { label: "Sent · awaiting client", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  in_progress: { label: "In progress", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  action_needed: { label: "Needs your input", cls: "bg-red-50 text-red-700 border-red-200" },
  delivered: { label: "Delivered", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function OwnerDashboard({ token, onInvalidToken }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const invalidRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/manage/${token}`);
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 404 && !invalidRef.current) {
        invalidRef.current = true;
        onInvalidToken?.();
      }
    }
  }, [token, onInvalidToken]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) {
    return (
      <div data-testid="owner-dashboard-loading" className="mx-auto max-w-7xl px-6 md:px-12 py-24 flex items-center justify-center gap-3 text-slate-500">
        <Loader2 className="animate-spin" size={20} /> Loading your agent…
      </div>
    );
  }

  const { agent, proposals, counts, earned_inr } = data;
  const pending = proposals.filter((p) => p.status === "pending_review");
  const actionNeeded = proposals.filter((p) => p.status === "action_needed");
  const pipeline = proposals.filter((p) => !["pending_review", "rejected"].includes(p.status));

  const manageUrl = `${window.location.origin}/dashboard?token=${token}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(manageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div data-testid="owner-dashboard" className="bg-white">
      <div className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-3 flex items-center gap-3 flex-wrap">
          <span className="relative inline-flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-emerald-500/40 animate-ping" />
            <span className="relative inline-block w-2 h-2 rounded-full bg-emerald-500" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            Your agent · live and monitoring the marketplace
          </span>
          <button
            type="button"
            onClick={copyLink}
            data-testid="owner-copy-link-btn"
            className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-blue-700 hover:text-blue-900 transition"
          >
            {copied ? <Check size={12} strokeWidth={2.6} /> : <Copy size={12} strokeWidth={2.4} />}
            {copied ? "Copied" : "Copy owner link"}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-16">
        {/* HEADER */}
        <header data-testid="owner-agent-header" className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="flex items-start gap-5">
            <AgentAvatar seed={agent.avatar_seed || agent.name} size={72} />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-600 inline-flex items-center gap-2">
                {agent.primary_role}
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 normal-case tracking-normal font-bold">
                  <BadgeCheck size={11} strokeWidth={2.6} /> Active
                </span>
              </p>
              <h1 data-testid="owner-agent-name" className="mt-2 font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-slate-900 leading-[0.95]">
                {agent.name}
              </h1>
              <p className="mt-2 text-sm text-slate-500 max-w-md">{agent.tagline}</p>
            </div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 max-w-sm">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700 inline-flex items-center gap-1.5">
              <ShieldCheck size={12} strokeWidth={2.6} /> Save your owner link
            </p>
            <p className="mt-1 text-xs text-amber-800/90 leading-relaxed">
              This page is only reachable through your private owner link — no login, no password. Copy it somewhere safe.
            </p>
          </div>
        </header>

        {/* STATS */}
        <section data-testid="owner-stats" className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-b border-slate-200 py-8">
          <OwnerStat testid="owner-stat-pending" label="Awaiting approval" value={counts.pending_review || 0} accent={counts.pending_review > 0} />
          <OwnerStat testid="owner-stat-active" label="Active jobs" value={(counts.sent || 0) + (counts.in_progress || 0) + (counts.action_needed || 0)} />
          <OwnerStat testid="owner-stat-delivered" label="Delivered" value={counts.delivered || 0} />
          <OwnerStat testid="owner-stat-earned" label="Earned" value={fmtINR(earned_inr)} />
        </section>

        {/* ACTION NEEDED */}
        {actionNeeded.length > 0 && (
          <section data-testid="action-items-section" className="mt-12">
            <SectionTitle icon={<AlertTriangle size={18} className="text-red-600" strokeWidth={2.4} />} title="Your agent needs you" sub="It can't proceed without your input on these." />
            <div className="mt-6 space-y-4">
              {actionNeeded.map((p) => (
                <ActionCard key={p.id} proposal={p} token={token} onDone={load} />
              ))}
            </div>
          </section>
        )}

        {/* PENDING REVIEW */}
        <section data-testid="review-section" className="mt-12">
          <SectionTitle icon={<Inbox size={18} className="text-blue-600" strokeWidth={2.4} />} title="Proposals awaiting your approval" sub="Your agent drafted these for matching client briefs. Nothing goes out without your sign-off." />
          {pending.length === 0 ? (
            <div data-testid="review-empty" className="mt-6 rounded-2xl border border-dashed border-slate-300 px-6 py-10 text-center">
              <Radar size={26} className="mx-auto text-blue-500 animate-pulse" />
              <p className="mt-3 font-display font-bold text-slate-900">Scanning the marketplace…</p>
              <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
                Your agent is watching for briefs that match <span className="font-semibold text-slate-700">{agent.category}</span> work and your skills. When one lands, a drafted proposal appears here for your approval.
              </p>
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              {pending.map((p) => (
                <ReviewCard key={p.id} proposal={p} token={token} onDone={load} />
              ))}
            </div>
          )}
        </section>

        {/* PIPELINE */}
        {pipeline.length > 0 && (
          <section data-testid="pipeline-section" className="mt-14">
            <SectionTitle icon={<Send size={18} className="text-indigo-600" strokeWidth={2.4} />} title="Pipeline" sub="Approved proposals and live projects your agent is running." />
            <div className="mt-6 space-y-5">
              {pipeline.map((p) => (
                <PipelineCard key={p.id} proposal={p} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ icon, title, sub }) {
  return (
    <div>
      <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-slate-900 inline-flex items-center gap-2.5">
        {icon} {title}
      </h2>
      <p className="mt-1 text-sm text-slate-500">{sub}</p>
    </div>
  );
}

function OwnerStat({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col gap-2">
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">{label}</p>
      <p className={`font-display text-3xl md:text-5xl font-black tracking-tighter leading-none ${accent ? "text-blue-600" : "text-slate-900"}`}>
        {value}
      </p>
    </div>
  );
}

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.sent;
  return (
    <span data-testid={`status-pill-${status}`} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${m.cls}`}>
      {m.label}
    </span>
  );
}

/* ---------- Proposal review (approve / edit / reject) ---------- */

function ReviewCard({ proposal: p, token, onDone }) {
  const [mode, setMode] = useState("view"); // view | edit
  const [cover, setCover] = useState(p.cover_letter || "");
  const [amount, setAmount] = useState(p.amount_inr);
  const [busy, setBusy] = useState(null); // approve | reject
  const [error, setError] = useState(null);

  const act = async (kind) => {
    setBusy(kind);
    setError(null);
    try {
      if (kind === "reject") {
        await api.post(`/manage/${token}/proposals/${p.id}/reject`);
      } else {
        const body = {};
        if (mode === "edit") {
          if (cover.trim() !== (p.cover_letter || "").trim()) body.cover_letter = cover.trim();
          const amt = parseInt(amount, 10);
          if (!Number.isNaN(amt) && amt !== p.amount_inr) body.amount_inr = amt;
        }
        await api.post(`/manage/${token}/proposals/${p.id}/approve`, body);
      }
      onDone?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <article data-testid={`review-card-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-[0_4px_20px_-14px_rgba(15,23,42,0.25)]">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <StatusPill status={p.status} />
          <h3 className="mt-2 font-display text-lg md:text-xl font-black tracking-tight text-slate-900 leading-snug">
            {p.brief_title}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {p.client_name}{p.client_company ? ` · ${p.client_company}` : ""} · client budget {fmtINR(p.budget_min)}–{fmtINR(p.budget_max)}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Agent's quote</p>
          {mode === "edit" ? (
            <div className="mt-1 flex items-center gap-1 justify-end">
              <IndianRupee size={14} className="text-slate-500" />
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid={`edit-amount-input-${p.id}`}
                className="w-28 px-2 py-1 rounded-md border border-slate-300 font-display font-bold text-slate-900 text-right outline-none focus:border-blue-400"
              />
            </div>
          ) : (
            <p className="font-display text-2xl font-black tracking-tight text-slate-900">{fmtINR(p.amount_inr)}</p>
          )}
          <p className="text-[11px] text-slate-500">{p.timeline_days} days</p>
        </div>
      </div>

      {mode === "edit" ? (
        <textarea
          value={cover}
          onChange={(e) => setCover(e.target.value)}
          rows={7}
          data-testid={`edit-cover-input-${p.id}`}
          className="mt-4 w-full px-3.5 py-3 text-sm rounded-xl border border-blue-300 bg-blue-50/30 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 resize-y leading-relaxed"
        />
      ) : (
        <p className="mt-4 text-sm text-slate-700 leading-relaxed whitespace-pre-line rounded-xl bg-slate-50/70 border border-slate-100 px-4 py-3.5">
          {p.cover_letter}
        </p>
      )}

      {p.milestones?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {p.milestones.map((m, i) => (
            <span key={i} className="text-[11px] px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 font-medium">
              {i + 1}. {m}
            </span>
          ))}
        </div>
      )}

      {error && <p data-testid={`review-error-${p.id}`} className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-5 flex flex-wrap gap-2.5">
        <button
          type="button"
          onClick={() => act("approve")}
          disabled={!!busy}
          data-testid={`approve-btn-${p.id}`}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 text-white text-sm font-bold px-4 py-2.5 hover:bg-blue-700 disabled:opacity-60 transition shadow-[0_14px_30px_-14px_rgba(37,99,235,0.7)]"
        >
          {busy === "approve" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2.5} />}
          {mode === "edit" ? "Approve edits & send" : "Approve & send to client"}
        </button>
        {mode === "view" ? (
          <button
            type="button"
            onClick={() => setMode("edit")}
            disabled={!!busy}
            data-testid={`edit-btn-${p.id}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white text-sm font-medium px-4 py-2.5 text-slate-700 hover:border-blue-400 hover:text-blue-700 transition"
          >
            <Pencil size={14} /> Edit first
          </button>
        ) : (
          <button
            type="button"
            onClick={() => { setMode("view"); setCover(p.cover_letter || ""); setAmount(p.amount_inr); }}
            data-testid={`cancel-edit-btn-${p.id}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white text-sm font-medium px-4 py-2.5 text-slate-700 hover:border-slate-400 transition"
          >
            <X size={14} /> Discard edits
          </button>
        )}
        <button
          type="button"
          onClick={() => act("reject")}
          disabled={!!busy}
          data-testid={`reject-btn-${p.id}`}
          className="inline-flex items-center gap-1.5 rounded-md text-sm font-medium px-4 py-2.5 text-slate-500 hover:text-red-600 transition ml-auto"
        >
          {busy === "reject" ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />} Reject
        </button>
      </div>
    </article>
  );
}

/* ---------- Action item (agent is blocked) ---------- */

function ActionCard({ proposal: p, token, onDone }) {
  const item = (p.action_items || []).find((a) => a.status === "open");
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!item) return null;

  const send = async () => {
    if (response.trim().length < 2) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/manage/${token}/proposals/${p.id}/actions/${item.id}/respond`, { response: response.trim() });
      onDone?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send your response.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article data-testid={`action-item-${item.id}`} className="rounded-2xl border border-red-200 bg-red-50/40 p-5 md:p-6">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center w-9 h-9 rounded-xl bg-red-100 text-red-600 shrink-0">
          <AlertTriangle size={17} strokeWidth={2.4} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-red-600">
            Blocked · {p.brief_title}
          </p>
          <h3 className="mt-1 font-display text-lg font-black tracking-tight text-slate-900">{item.title}</h3>
          <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{item.description}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
        <textarea
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          rows={2}
          placeholder="Type your answer — the agent picks it up instantly and continues the work…"
          data-testid={`action-response-input-${item.id}`}
          className="flex-1 px-3.5 py-2.5 text-sm rounded-xl border border-slate-300 bg-white outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 resize-none"
        />
        <button
          type="button"
          onClick={send}
          disabled={busy || response.trim().length < 2}
          data-testid={`action-respond-btn-${item.id}`}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-md bg-slate-900 text-white text-sm font-bold px-4 py-2.5 hover:bg-slate-800 disabled:opacity-50 transition"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} strokeWidth={2.5} />}
          Unblock agent
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </article>
  );
}

/* ---------- Pipeline (sent / in progress / delivered) ---------- */

function PipelineCard({ proposal: p }) {
  const [openLog, setOpenLog] = useState(p.status !== "delivered");
  const events = p.events || [];

  return (
    <article data-testid={`pipeline-card-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <StatusPill status={p.status} />
          <h3 className="mt-2 font-display text-lg font-black tracking-tight text-slate-900 leading-snug">{p.brief_title}</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {p.client_name}{p.client_company ? ` · ${p.client_company}` : ""}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-display text-xl font-black tracking-tight text-slate-900">{fmtINR(p.amount_inr)}</p>
          <p className="text-[11px] text-slate-500">{p.timeline_days} days</p>
        </div>
      </div>

      {p.deliverables && (
        <div data-testid={`deliverables-${p.id}`} className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3.5">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">Delivered package</p>
          <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{p.deliverables.note}</p>
          <ul className="mt-2 space-y-1">
            {p.deliverables.items?.map((it, i) => (
              <li key={i} className="text-xs text-emerald-800 font-mono flex items-start gap-1.5">
                <Check size={12} className="mt-0.5 shrink-0" strokeWidth={2.6} /> {it}
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpenLog((v) => !v)}
        data-testid={`toggle-log-${p.id}`}
        className="mt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 hover:text-blue-600 transition"
      >
        {openLog ? "Hide activity log" : `Show activity log (${events.length})`}
      </button>
      {openLog && (
        <ol className="mt-3 relative border-l border-slate-200 ml-1 space-y-4">
          {events.slice().reverse().map((e) => (
            <li key={e.id} className="relative pl-5">
              <span className={`absolute -left-[4.5px] top-1.5 w-2 h-2 rounded-full ring-4 ring-white ${
                e.type === "flag" ? "bg-red-500" : e.type === "payment" ? "bg-emerald-500" : e.type === "delivery" ? "bg-emerald-600" : e.type === "freelancer" ? "bg-slate-900" : "bg-blue-500"
              }`} />
              <p className="text-sm text-slate-700 leading-snug">{e.text}</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 mt-0.5">
                {new Date(e.ts).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                {e.internal ? " · only you can see this" : ""}
              </p>
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}
